import { useEffect, useState } from 'react';
import {
  approveMaterialForSlides,
  getManualAiPrompt,
  getTrainingMaterial,
  importManualMaterial,
  materialSlideMediaUrl,
  startMaterialGeneration,
  type TrainingMaterial,
} from './api';
import { SlideVisual } from './SlideVisual';
import { GoogleSlidesPanel } from './GoogleSlidesPanel';

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function representativeTestQuestions<T extends { question: string }>(questions: T[]): T[] {
  const selected: T[] = [];
  const seen = new Set<string>();
  for (const question of questions) {
    const key = question.question
      .normalize('NFKC')
      .replace(/\([0-9]+\)(?=[。.!！?？]?$)/u, '')
      .replace(/\s+/gu, '')
      .toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    selected.push(question);
    if (selected.length === 5) break;
  }
  return selected;
}

export function MaterialDraftPanel({
  companyId,
  csrfToken,
  onError,
  projectId,
  requestId,
}: {
  companyId: string;
  csrfToken: string;
  onError: (error: unknown) => void;
  projectId: string;
  requestId: string;
}) {
  const [material, setMaterial] = useState<TrainingMaterial | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [approvalReason, setApprovalReason] = useState('');
  const [prompt, setPrompt] = useState<string | null>(null);
  const [importJson, setImportJson] = useState('');
  const [generationKey, setGenerationKey] = useState(() => crypto.randomUUID());
  const [importKey, setImportKey] = useState(() => crypto.randomUUID());

  async function refresh(): Promise<TrainingMaterial | null> {
    const result = await getTrainingMaterial(companyId, projectId, requestId);
    setMaterial(result);
    return result;
  }

  useEffect(() => {
    let active = true;
    setMaterial(null);
    setLoading(true);
    setNotice(null);
    setPrompt(null);
    setImportJson('');
    setGenerationKey(crypto.randomUUID());
    setImportKey(crypto.randomUUID());
    void getTrainingMaterial(companyId, projectId, requestId)
      .then((result) => {
        if (active) setMaterial(result);
      })
      .catch((error: unknown) => {
        if (active) onError(error);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId, projectId, requestId]);

  async function generate(): Promise<void> {
    setBusy(true);
    setNotice('承認済みコンテンツからDRAFTを生成しています。');
    try {
      await startMaterialGeneration(
        companyId,
        projectId,
        requestId,
        csrfToken,
        generationKey,
        overrideReason.trim() || null,
      );
      for (let attempt = 0; attempt < 45; attempt += 1) {
        await wait(2000);
        const current = await refresh();
        if (current?.job?.status === 'SUCCEEDED' || current?.job?.status === 'FAILED') break;
        setNotice(`教材を生成しています（${Math.min((attempt + 1) * 2, 90)}秒経過）…`);
      }
      const current = await refresh();
      if (current?.job?.status === 'FAILED') {
        setGenerationKey(crypto.randomUUID());
        setNotice(
          current.job.errorDetail ?? '生成に失敗しました。コンテンツ不足を確認してください。',
        );
      } else if (current?.revision !== null && current?.revision !== undefined) {
        setNotice(
          '教材DRAFTを生成しました。スライド本文・画像・設問・話者ノートを確認してください。',
        );
        setGenerationKey(crypto.randomUUID());
      } else {
        setNotice('生成を受け付けました。しばらくしてから「状態を更新」を押してください。');
      }
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '教材生成を開始できませんでした。');
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function approve(): Promise<void> {
    if (material?.revision === null || material?.revision === undefined) return;
    setBusy(true);
    try {
      await approveMaterialForSlides(
        companyId,
        projectId,
        material.revision.id,
        csrfToken,
        approvalReason.trim() || null,
      );
      await refresh();
      setNotice('DRAFT内容を承認しました。次PHASEのGoogle Slides生成対象になります。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function preparePrompt(): Promise<void> {
    setBusy(true);
    try {
      const next = await getManualAiPrompt(companyId, projectId, requestId);
      setPrompt(next);
      await navigator.clipboard.writeText(next);
      setNotice(
        '個人情報を伏せた品質改善プロンプトをコピーしました。外部AIへの自動送信はありません。',
      );
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function importJsonDraft(): Promise<void> {
    setBusy(true);
    try {
      const parsed: unknown = JSON.parse(importJson);
      await importManualMaterial(companyId, projectId, requestId, parsed, csrfToken, importKey);
      setImportKey(crypto.randomUUID());
      await refresh();
      setNotice('JSONを厳格検証し、DRAFTとして取り込みました。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  const revision = material?.revision ?? null;
  const durationMinutes = revision === null ? 0 : revision.totalEstimatedSeconds / 60;
  const compositionErrors =
    revision?.slides.filter((slide) => slide.composition?.fitStatus === 'ERROR').length ?? 0;
  const meaningfulVisuals =
    revision?.slides.filter((slide) => slide.composition?.meaningfulVisual === true).length ?? 0;
  const narrationQualityError = revision?.narrationSummary?.qualityStatus === 'ERROR';

  return (
    <section className="material-draft-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">ONE COLOR DRAFT REVIEW</p>
          <h3>教材生成・内容確認</h3>
        </div>
        <button
          className="secondary"
          disabled={busy || loading}
          onClick={() => void refresh().catch(onError)}
          type="button"
        >
          状態を更新
        </button>
      </div>

      {notice !== null && <div className="notice warning">{notice}</div>}
      {material?.job?.status === 'FAILED' && (
        <div className="notice warning">
          <strong>{material.job.errorCode}</strong>
          <span>{material.job.errorDetail}</span>
        </div>
      )}

      <p className="muted">
        外部AI APIは呼び出しません。承認済み部品だけで決定論的に生成し、完全一致は停止します。
      </p>
      <label>
        高類似時の管理者理由（通常は空欄）
        <textarea
          maxLength={1000}
          onChange={(event) => setOverrideReason(event.target.value)}
          placeholder="過去教材との類似度60%以上を例外承認する場合のみ10文字以上で入力"
          value={overrideReason}
        />
      </label>
      {loading && <div className="material-loading">生成済み教材を読み込んでいます…</div>}
      <button disabled={busy || loading} onClick={() => void generate()} type="button">
        {loading
          ? '教材状態を読み込み中…'
          : busy
            ? '生成処理中…'
            : revision === null
              ? '教材DRAFTを生成'
              : '別構成で再生成'}
      </button>

      {revision !== null && (
        <div className="material-review">
          <dl className="detail-list">
            <div>
              <dt>教材ID</dt>
              <dd>{material?.displayId}</dd>
            </div>
            <div>
              <dt>版</dt>
              <dd>第{revision.revisionNo}版</dd>
            </div>
            <div>
              <dt>スライド</dt>
              <dd>{revision.slides.length}枚</dd>
            </div>
            <div>
              <dt>想定時間</dt>
              <dd>{durationMinutes.toFixed(1)}分</dd>
            </div>
            <div>
              <dt>意味のある視覚</dt>
              <dd>
                {meaningfulVisuals}/{revision.slides.length}枚
              </dd>
            </div>
          </dl>
          {revision.sourceRecommendation !== null && (
            <section aria-label="公的情報の関連度判定">
              <h4>公的情報の関連度判定</h4>
              <p className="muted">
                承認・有効期間・利用許諾を通過した情報を、テーマ30点、工種20点、機械20点、
                地域15点、季節10点、新しさ5点の100点満点で判定しています。60点未満は教材へ入りません。
              </p>
              {revision.sourceRecommendation.issueCodes.includes('NO_RELEVANT_APPROVED_SOURCE') && (
                <div className="notice warning">
                  <strong>関連する承認済み公的情報はありません</strong>
                  <span>
                    無関係な情報は差し込んでいません。従来の承認済み安全マスターだけで構成しています。
                  </span>
                </div>
              )}
              {revision.sourceRecommendation.issueCodes.includes(
                'CRITICAL_REUSE_ANGLE_REQUIRED',
              ) && (
                <div className="notice warning">
                  CRITICAL情報の前月再利用には、異なる説明角度の記録が必要なため候補から除外しました。
                </div>
              )}
              <p className="muted">
                評価 {revision.sourceRecommendation.evaluatedCount}件／60点以上{' '}
                {revision.sourceRecommendation.eligibleCount}件／採用{' '}
                {revision.sourceRecommendation.selected.length}件
              </p>
              {revision.sourceRecommendation.selected.length > 0 && (
                <div className="question-review-list">
                  {revision.sourceRecommendation.selected.map((source) => (
                    <article className="question-preview-card" key={source.itemId}>
                      <h5>
                        第{source.rank}候補・{source.totalScore}点 {source.title}
                      </h5>
                      <p>{source.reasonLabels.join('／')}</p>
                      {source.previousMonthUsed && (
                        <p className="muted">同一工事の前月教材で使用済みのため同点内で降格</p>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}
          {compositionErrors > 0 && (
            <div className="notice warning">
              組版エラーが{compositionErrors}
              枚あります。文字量または配置を修正して再生成してください。
            </div>
          )}
          {(durationMinutes < 8 || durationMinutes > 10) && (
            <div className="notice warning">
              想定時間が8〜10分の範囲外です。話者ノートを確認してください。
            </div>
          )}
          {revision.narrationSummary !== null && (
            <>
              <div
                className={`notice ${
                  revision.narrationSummary.qualityStatus === 'PASS' ? 'success' : 'warning'
                }`}
              >
                <strong>
                  ナレーション／動画台本 品質検証 {revision.narrationSummary.qualityStatus}
                </strong>
                <span>
                  合計{Math.floor(revision.narrationSummary.totalSeconds / 60)}分
                  {revision.narrationSummary.totalSeconds % 60}秒／本文読み上げ判定{' '}
                  {revision.narrationSummary.bodyCopyCount}件／ページ遷移{' '}
                  {Math.round(revision.narrationSummary.transitionCoverage * 100)}%
                </span>
                <span>
                  冒頭の会社名・工事名・対象年月:{' '}
                  {revision.narrationSummary.openingIdentityComplete ? '確認済み' : '不足'}
                </span>
                <span>
                  区間: 導入 {revision.narrationSummary.segmentSeconds.INTRO}秒／事故構造{' '}
                  {revision.narrationSummary.segmentSeconds.ACCIDENT_STRUCTURE}秒／防止対策{' '}
                  {revision.narrationSummary.segmentSeconds.PREVENTION}秒／KY・確認問題{' '}
                  {revision.narrationSummary.segmentSeconds.KY_QUIZ}秒／まとめ{' '}
                  {revision.narrationSummary.segmentSeconds.SUMMARY}秒
                </span>
                {revision.narrationSummary.qualityIssues.map((issue) => (
                  <span key={issue}>{issue}</span>
                ))}
              </div>
              <details className="manual-ai-panel">
                <summary>時間付きナレーション台本を確認</summary>
                <pre>{revision.narrationSummary.timedScript}</pre>
              </details>
            </>
          )}
          {revision.similarityWarnings.map((warning) => (
            <div className="notice warning" key={warning}>
              {warning}
            </div>
          ))}

          <h4>スライド構成・本文・話者ノート</h4>
          <p className="muted">
            全スライドを展開表示しています。画像が関連付けられていない場合も画像枠に明示します。
          </p>
          <div className="slide-review-list">
            {revision.slides.map((slide) => (
              <article className="slide-preview-card" key={slide.id}>
                <div className="slide-preview-meta">
                  <strong>スライド {slide.slideNo}</strong>
                  <span>
                    {slide.composition?.layoutFamily ?? 'LEGACY'} ·{' '}
                    {slide.composition?.visualKind ?? '未設定'} · {slide.estimatedSeconds}秒
                  </span>
                </div>
                {slide.composition?.fitIssues.map((issue) => (
                  <div className="notice warning" key={issue}>
                    {issue}
                  </div>
                ))}
                <div className="slide-canvas">
                  <div className="slide-canvas-heading">
                    <div>
                      <span>{String(slide.slideNo).padStart(2, '0')}</span>
                      <small>{slide.type.replaceAll('_', ' ')}</small>
                    </div>
                    <h5>{slide.title}</h5>
                  </div>
                  <SlideVisual
                    slide={slide}
                    {...(slide.mediaAssetId === null || slide.mediaAssetId === undefined
                      ? {}
                      : {
                          mediaUrl: materialSlideMediaUrl(
                            companyId,
                            projectId,
                            revision.id,
                            slide.id,
                          ),
                        })}
                  />
                  <div className="slide-canvas-footer">
                    <span>{material?.displayId}</span>
                    <span>ONE COLOR · DRAFT</span>
                  </div>
                </div>
                <div className="speaker-notes">
                  <strong>話者ノート</strong>
                  {slide.narration !== null && (
                    <span className="muted">
                      区間: {slide.narration.segment}／本文重複{' '}
                      {Math.round(slide.narration.bodyOverlapScore * 100)}%
                    </span>
                  )}
                  <p>{slide.speakerNotes}</p>
                </div>
              </article>
            ))}
          </div>

          <h4>KY問いかけ</h4>
          {revision.kyQuizSummary !== null && (
            <div
              className={`notice ${
                revision.kyQuizSummary.qualityStatus === 'PASS' ? 'success' : 'warning'
              }`}
            >
              KY {revision.kyQuizSummary.kyCount}件／確認問題 {revision.kyQuizSummary.quizCount}問／
              {revision.kyQuizSummary.quizTypeCount}形式／品質検証{' '}
              {revision.kyQuizSummary.qualityStatus}
              {revision.kyQuizSummary.sourceMode === 'KNOWLEDGE_PACK'
                ? '（承認済みKnowledge Pack）'
                : '（従来モジュール）'}
            </div>
          )}
          <div className="question-review-list">
            {revision.kyQuestions.map((item, index) => (
              <article className="question-preview-card" key={item.sortOrder}>
                <h5>
                  KY {index + 1}. {item.title ?? item.question}
                </h5>
                {item.situation !== null && <p>状況: {item.situation}</p>}
                <p>
                  <strong>問い:</strong> {item.question}
                </p>
                {item.dangerPoints.length > 0 && (
                  <p>危険ポイント: {item.dangerPoints.join('／')}</p>
                )}
                {item.spotterPosition !== null && <p>誘導員位置: {item.spotterPosition}</p>}
                {item.vehicleRoute !== null && <p>車両経路: {item.vehicleRoute}</p>}
                {item.workerRoute !== null && <p>作業員経路: {item.workerRoute}</p>}
                {item.exclusionZone !== null && <p>立入禁止範囲: {item.exclusionZone}</p>}
                {item.stopCondition !== null && <p>停止条件: {item.stopCondition}</p>}
                {item.modelPoints.length > 0 && <p>模範ポイント: {item.modelPoints.join('／')}</p>}
                {item.diagramCode !== null && <p>承認済み図解: {item.diagramCode}</p>}
                {item.tags.length > 0 && <p className="muted">タグ: {item.tags.join('・')}</p>}
              </article>
            ))}
          </div>

          <h4>確認問題</h4>
          <p className="muted">
            Knowledge Pack利用時は、重複を除き2形式以上になるよう選定した5問を表示・納品します。
          </p>
          <div className="question-review-list">
            {representativeTestQuestions(revision.testQuestions).map((question, index) => (
              <article className="question-preview-card" key={question.sortOrder}>
                <h5>
                  問{index + 1}. {question.question}
                </h5>
                {question.questionType !== null && (
                  <p className="muted">
                    形式: {question.questionType}／難易度: {question.difficulty}／関連:{' '}
                    {question.relatedItemCode}
                  </p>
                )}
                <ol>
                  {question.choices.map((choice) => (
                    <li key={choice}>{choice}</li>
                  ))}
                </ol>
                <p>
                  正解: {question.correctChoiceNo}／{question.explanation}
                </p>
                {question.distractorRationales.length > 0 && (
                  <p>誤答理由: {question.distractorRationales.join('／')}</p>
                )}
                {question.diagramCode !== null && <p>承認済み図解: {question.diagramCode}</p>}
                {question.tags.length > 0 && (
                  <p className="muted">タグ: {question.tags.join('・')}</p>
                )}
              </article>
            ))}
          </div>

          {revision.approvedForSlidesAt === null ? (
            <div className="approval-panel">
              <label>
                承認理由（高類似警告時は10文字以上）
                <textarea
                  maxLength={1000}
                  onChange={(event) => setApprovalReason(event.target.value)}
                  value={approvalReason}
                />
              </label>
              <button
                disabled={busy || compositionErrors > 0 || narrationQualityError}
                onClick={() => void approve()}
                type="button"
              >
                DRAFT内容を承認
              </button>
            </div>
          ) : (
            <div className="notice success">DRAFT内容承認済み（Google Slides生成待ち）</div>
          )}

          <GoogleSlidesPanel
            approved={revision.approvedForSlidesAt !== null}
            companyId={companyId}
            csrfToken={csrfToken}
            narrationText={
              revision.narrationSummary?.timedScript ??
              revision.slides
                .map((slide) => `スライド${slide.slideNo}「${slide.title}」\n${slide.speakerNotes}`)
                .join('\n\n')
            }
            onError={onError}
            projectId={projectId}
            revisionId={revision.id}
          />
        </div>
      )}

      <details className="manual-ai-panel">
        <summary>手動AIで教材品質を改善する（任意）</summary>
        <p className="muted">
          1. プロンプトをコピー、2. 管理者がChatGPTまたはGeminiへ手動貼付、3.
          返却されたJSONを取り込み、4. 新しいDRAFTを全ページ確認、の順で使用します。
          システムから外部AIへの自動通信はありません。
        </p>
        <p className="muted">
          会社名・工事名・担当者名・自由記述・ヒヤリハット原文はプロンプトに含めません。
          承認済み教材、安全根拠ID、匿名化した構成、工種と機械分類だけを含めます。
        </p>
        <button
          className="secondary"
          disabled={busy}
          onClick={() => void preparePrompt()}
          type="button"
        >
          品質改善プロンプトをコピー
        </button>
        {prompt !== null && <textarea className="prompt-preview" readOnly value={prompt} />}
        <label>
          返却JSON（1MB以下）
          <textarea
            className="json-import"
            onChange={(event) => setImportJson(event.target.value)}
            value={importJson}
          />
        </label>
        <button
          disabled={busy || importJson.trim().length === 0}
          onClick={() => void importJsonDraft()}
          type="button"
        >
          厳格検証してDRAFT取込
        </button>
      </details>
    </section>
  );
}
